import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { RichTextEditor } from "@/components/common/rich-text-editor";
import { SanitizedHtml } from "@/components/common/sanitized-html";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { getHelpNode, saveHelpNode } from "@/features/knowledge/api";

interface HelpPopupProps {
  nodeKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Contextual help for a screen, keyed by `nodeKey`. Any user sees the stored
 * help text in their language (EN/ES resolved server-side); a platform admin can
 * edit it inline. An unknown node renders empty text (never a 404), matching the
 * legacy behavior.
 */
export function HelpPopup({ nodeKey, open, onOpenChange }: HelpPopupProps) {
  const isPlatformAdmin = useAuthStore((s) => s.user?.is_platform_admin ?? false);
  const lang = useAuthStore((s) => s.user?.language ?? "en");
  const [loading, setLoading] = useState(false);
  const [helpText, setHelpText] = useState("");
  const [editing, setEditing] = useState(false);
  const [draftEn, setDraftEn] = useState("");
  const [draftEs, setDraftEs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setEditing(false);
    setLoading(true);
    getHelpNode(nodeKey, lang)
      .then((node) => {
        if (cancelled) return;
        setHelpText(node.helpText);
        setDraftEn(node.helpTextEn);
        setDraftEs(node.helpTextEs);
      })
      .catch(() => !cancelled && toast.error("Could not load help content."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, nodeKey, lang]);

  async function handleSave() {
    setSaving(true);
    try {
      const node = await saveHelpNode(nodeKey, { helpTextEn: draftEn, helpTextEs: draftEs });
      setHelpText(lang === "es" ? node.helpTextEs : node.helpTextEn);
      setEditing(false);
      toast.success("Help content saved.");
    } catch {
      toast.error("Could not save help content.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Help</DialogTitle>
          <DialogDescription>Guidance for this screen.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : editing ? (
          <div className="space-y-4">
            <div>
              <p className="mb-1 text-sm font-medium">English</p>
              <RichTextEditor value={draftEn} onChange={setDraftEn} />
            </div>
            <div>
              <p className="mb-1 text-sm font-medium">Spanish</p>
              <RichTextEditor value={draftEs} onChange={setDraftEs} />
            </div>
          </div>
        ) : helpText ? (
          <SanitizedHtml html={helpText} />
        ) : (
          <p className="text-muted-foreground text-sm">No help content for this screen yet.</p>
        )}

        {isPlatformAdmin && !loading && (
          <DialogFooter>
            {editing ? (
              <>
                <Button variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setEditing(true)}>
                Edit help text
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
